/* eslint-disable id-length */
import { useEffect, useMemo } from "react";
import { ChartData, TooltipModel } from "chart.js";

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "app/components/shared/HoverCard";
import { useChart } from "app/components/shared/Chart/chart";

import LineChart, { LineChartUnit } from "../LineChart";
import SparkLineChart, { withGradientFillLine } from "../SparkLineChart";
import { Card, CardSection, CardChart, CardLabel, CardStat } from "../Card";
import { colorsForIndex, ThemeDefaultTrend } from "../Theme";
import { TimePeriod } from "../Renderer";
import {
  calculateSum,
  formatPercentage,
  getTimePeriodString,
  formatTooltipDateString,
} from "./utils";
import { ChartLegend } from "../ChartLegend";

type JobPassRateDatapoint = {
  time_bucket: number;
  passed: number;
  failed: number;
  total: number;
  percentage: number | null;
};

interface Props {
  // eslint-disable-next-line react/no-unused-prop-types
  scope: "queue" | "cluster" | "organization";
  datasets: Array<{
    label: string;
    data: Array<JobPassRateDatapoint>;
  }>;
}

const percentFormat = new Intl.NumberFormat("default", {
  style: "percent",
});

function sumAcrossDatasets(
  datasets: Props["datasets"],
  fn: (item: JobPassRateDatapoint) => number,
): number {
  return datasets.reduce((acc, dataset) => {
    return acc + dataset.data.reduce((acc, item) => acc + fn(item), 0);
  }, 0);
}

function sumJobs(datasets: Props["datasets"]): number {
  return sumAcrossDatasets(datasets, (item) => item.total);
}

function sumFailedJobs(datasets: Props["datasets"]): number {
  return sumAcrossDatasets(datasets, (item) => item.failed);
}

export function JobPassRateSpark({ datasets }: Props) {
  const totalJobs = sumJobs(datasets);
  const totalFailed = sumFailedJobs(datasets);
  const percentage =
    totalJobs === 0 ? null : ((totalJobs - totalFailed) / totalJobs) * 100;

  return (
    <Card>
      <CardSection>
        <CardLabel>Job pass rate</CardLabel>
      </CardSection>

      <CardSection>
        <CardStat
          value={percentage ? percentage.toFixed(2) + "%" : "N/A"}
          size="lg"
          layout="stacked"
        />

        <CardSection className="flex-col">
          <CardStat label="Total" value={totalJobs.toLocaleString()} />
          <CardStat label="Failed" value={totalFailed.toLocaleString()} />
        </CardSection>
      </CardSection>

      <CardChart>
        <SparkLineChart
          data={{
            labels: datasets[0].data.map((item) => item.time_bucket),
            datasets: [
              withGradientFillLine(
                datasets[0].data.map((item) => item.percentage),
                ThemeDefaultTrend,
              ),
            ],
          }}
        />
      </CardChart>
    </Card>
  );
}

function formatPassRateForDataset(dataset: JobPassRateDatapoint[]): string {
  const total = calculateSum(dataset, "total");
  const passed = calculateSum(dataset, "passed");
  return total !== 0 ? percentFormat.format(passed / total) : "–";
}

export function JobPassRateDetail({
  scope,
  datasets,
  timePeriod,
}: Props & { timePeriod: TimePeriod }) {
  const data: ChartData = useMemo(
    () => ({
      labels: datasets[0].data.map((item) => item.time_bucket),
      datasets: datasets.map((dataset, index) => {
        return {
          label: dataset.label,
          data: dataset.data.map((item) =>
            item.percentage !== null ? item.percentage : null,
          ),
          ...colorsForIndex(scope === "queue" ? index + 2 : index),
        };
      }),
    }),
    [datasets],
  );

  const totalJobs = sumJobs(datasets);
  const totalFailed = sumFailedJobs(datasets);

  const { chartRef, updateData } = useChart();
  useEffect(() => {
    updateData(data);
  }, [data, updateData]);

  return (
    <Card className="lg:flex-row">
      <div className="flex flex-col grow gap-3 min-w-0">
        <CardSection>
          <CardLabel description={getTimePeriodString(timePeriod)}>
            Job pass rate
          </CardLabel>

          <CardSection className="flex-col">
            <CardStat value={totalJobs.toLocaleString()} label="Total Jobs" />
            <CardStat
              value={totalFailed.toLocaleString()}
              label="Total Failed"
            />
          </CardSection>
        </CardSection>

        <CardChart>
          <LineChart
            ref={chartRef}
            data={data}
            tooltip={Tooltip}
            units={{ y: LineChartUnit.percentage }}
          />
        </CardChart>
      </div>

      <ChartLegend
        datasets={data.datasets}
        chartRef={chartRef}
        columns={{
          "Pass rate": (_, index) => (
            <span>{formatPassRateForDataset(datasets[index].data)}</span>
          ),
          Failed: (_, index) => (
            <span>{calculateSum(datasets[index].data, "failed")}</span>
          ),
          Passed: (_, index) => (
            <span>{calculateSum(datasets[index].data, "passed")}</span>
          ),
          Total: (_, index) => (
            <span>{calculateSum(datasets[index].data, "total")}</span>
          ),
        }}
      />
    </Card>
  );
}

const Tooltip = ({ tooltip }: { tooltip: TooltipModel<"line"> }) => {
  const dateString = formatTooltipDateString(tooltip.dataPoints[0].parsed.x);

  return (
    <HoverCard open={true}>
      <HoverCardTrigger />
      <HoverCardContent side="right" className="w-auto px-2 py-2.5">
        <div className="flex flex-col gap-1.5">
          <div className="font-semibold text-xs">{dateString}</div>

          {tooltip.dataPoints.map(({ dataset, parsed }, index) => {
            return (
              <div
                key={index}
                className="font-semibold text-xs"
                style={{ color: dataset.borderColor as string }}
              >
                {dataset.label}:{" "}
                <span className="font-medium text-charcoal-300">
                  {formatPercentage(parsed.y)}
                </span>
              </div>
            );
          })}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};
